<template>
    <tr>
        <td>
            <b-form-checkbox
                    v-model="task.complete"
                    @change="onTaskCompletedChanged">&nbsp;
            </b-form-checkbox>
        </td>
        <td :class="{ 'global-text-line-through': task.complete }" v-b-tooltip.d250="task.description">
            <div>{{ task.subject }}</div>
        </td>
        <td :class="{ 'global-text-line-through': task.complete }">
            <div>{{ task.due | formatDate }}</div>
        </td>
        <td :class="{ 'global-text-line-through': task.complete }">
            <div>{{ task.modified | formatDateTime }}</div>
        </td>
        <td :class="{ 'global-text-line-through': task.complete }">
            <div :class="{ 'global-cursor-pointer': !task.complete, '': task.complete }" v-if="task.assignedTo">
                <span :class="{ 'project-tasks-assigned-to': !task.complete, '': task.complete }"
                      @click="editProjectMember()" v-show="projectPermissions.includes('UPDATE_TASK')">{{ assignedProjectMember }}</span>
                <span v-b-tooltip.hover.d250="$t('tasks.removeAssignee')" @click="removeProjectMember()" v-show="projectPermissions.includes('UPDATE_TASK')" style="margin-left: 10px;">
                    <icon name="times-circle" style="color: red"></icon>
                </span>
                <div v-if="getProjectMember().duplicate"><em><small>({{ getProjectMember().email }})</small></em></div>
            </div>
            <b-button size="sm" class="float-left mr-1 pb-0" v-else :title="$t('tasks.assignUser')"
                      @click.stop="selectProjectMember()" :disabled="task.complete">
                <icon name="user-plus"></icon>
            </b-button>
        </td>
        <td class="text-right">
            <b-btn-group>
                <b-button size="sm" class="pb-0" 
                          v-if="task.attachments.length > 0"
                          @click.stop="goToFile()"
                          v-b-tooltip.hover.d250="$t('files.context.goToFile')"
                          :title="$t('feed.actionButtons.goToFile')">
                    <icon name="file"></icon>
                </b-button>
                <b-button size="sm" class="pb-0" 
                          @click.stop="editTask()"
                          v-b-tooltip.hover.d250="$t('files.context.editTask')"
                          :disabled="task.complete || !projectPermissions.includes('UPDATE_TASK')">
                    <icon name="pencil"></icon>
                </b-button>
                <b-button size="sm" class="pb-0" 
                          @click.stop="deleteTask()"
                          v-b-tooltip.hover.d250="$t('files.context.deleteTask')"
                          :disabled="task.complete || !projectPermissions.includes('DELETE_TASK')">
                    <icon name="trash"></icon>
                </b-button>
            </b-btn-group>
        </td>
    </tr>
</template>

<script>
    import Vue from 'vue';
    import toastr from 'toastr';
    import {mapGetters} from 'vuex';
    import i18n from '../../../i18n.bootstrap';

    export default {
        name: 'task',

        props: ['task', 'taskListId'],

        data() {
            return {
                backupAssignedTo: null
            };
        },

        computed: {
            ...mapGetters({
                projectCollaborators: 'projects/projectCollaborators'
            }),

            assignedProjectMember() {
                return this.getProjectMember().displayName || this.task.assignedTo;
            },
            projectPermissions() {
              if(this.$store.state.projects.currentProject.projectData.role){
                return this.$store.state.projects.currentProject.projectData.role.permissions;
              } else {
                return [];
              }
            },
        },

        mounted() {
            this.$root.$on('dlgTypeahead_ProjectTasks::value-selected', (payload) => {
                if (payload.context.id === this.task.id) {
                    if (!payload.context.isAddCollaborator) {
                        // edit project member
                        this.backupAssignedTo = this.task.assignedTo;
                    }

                    let assigneeId = payload.selectedValue ? payload.selectedValue.id : '';
                    Vue.set(this.task, 'assignedTo', assigneeId);

                    this.$store.dispatch('projects/editTask', {
                        listId: this.taskListId,
                        task: this.task
                    }).then(() => {
                        this.$forceUpdate();
                        this.backupAssignedTo = null;
                        this.$root.$emit('bv::hide::modal', 'dlgTypeahead_ProjectTasks');
                    }).catch(() => {
                        toastr.error(i18n.t('tasks.messages.action.errorProjectMemberUpdate'));
                        this.task.assignedTo = this.backupAssignedTo;
                        this.backupAssignedTo = null;
                    });
                }
            });
        },

        beforeDestroy() {
            this.$root.$off('dlgTypeahead_ProjectTasks::value-selected');
        },

        methods: {

            onTaskCompletedChanged(isCompleted) {
                this.$store.dispatch('projects/toggleTaskComplete', {
                    listId: this.taskListId,
                    task: this.task
                }).then(() => {
                    this.task.complete = isCompleted;
                }).catch(() => {
                    toastr.error(i18n.t('tasks.messages.action.errorTaskComplete'));
                });
            },

            editTask() {
                this.$root.$emit('bv::show::modal', 'dlgTask');
                this.$root.$emit('project-tasks::edit-task', {task: this.task});
            },

            deleteTask() {
                const deleteTaskAction = () => {
                    this.$store.dispatch('projects/deleteTask', {
                        listId: this.taskListId,
                        task: this.task
                    });
                };

                this.$snotify.confirm(i18n.t('general.promptQuestion'), '', {
                    closeOnClick: true,
                    backdrop: 0.3,
                    buttons: [
                        {text: i18n.t('general.yes'), action: deleteTaskAction},
                        {text: i18n.t('general.no')}
                    ]
                });
            },

            goToFile() {
                let fileId = this.task.attachments[0];
                let projectId = this.$route.params.projectId;
                this.$router.push({ name:'project-files', 
                                    params: { projectId, fileId }, 
                                    query: { displayFileId: fileId } });
            },

            getProjectMember() {
                return this.projectCollaborators.find(m => m.id === this.task.assignedTo) || {};
            },

            selectProjectMember() {
                if (this.task.complete) {
                    return;
                }

                this.$root.$emit('bv::show::modal', 'dlgTypeahead_ProjectTasks');
                this.$root.$emit('project-tasks::select-collaborator', {task: this.task, isAddCollaborator: true});
            },

            editProjectMember() {
                if (this.task.complete) {
                    return;
                }

                this.$root.$emit('bv::show::modal', 'dlgTypeahead_ProjectTasks');
                this.$root.$emit('project-tasks::select-collaborator', {task: this.task, isAddCollaborator: false});
            },

            removeProjectMember() {
                if (this.task.complete) {
                    return;
                }

                const removeProjectMemberAction = () => {
                    this.backupAssignedTo = this.assignedTo;
                    this.task.assignedTo = '';

                    this.$store.dispatch('projects/editTask', {
                        listId: this.taskListId,
                        task: this.task
                    }).then(() => {
                        this.backupAssignedTo = null;
                    }).catch(() => {
                        toastr.error(i18n.t('tasks.messages.action.errorProjectMemberRemove'));
                        this.task.assignedTo = this.backupAssignedTo;
                        this.backupAssignedTo = null;
                    });
                };

                this.$snotify.confirm(i18n.t('general.promptQuestion'), '', {
                    closeOnClick: true,
                    backdrop: 0.3,
                    buttons: [
                        {text: i18n.t('general.yes'), action: removeProjectMemberAction},
                        {text: i18n.t('general.no')}
                    ]
                });
            }
        }
    }
</script>

<style>
    .project-tasks-assigned-to:hover {
        color: #0180c7;
    }
</style>
